import { useEffect, useState } from 'react';
import { campaignsAPI, legalCheckAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Plus, Search, Megaphone, Calendar as CalendarIcon, DollarSign, Edit, Trash2, Shield, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isLegalCheckOpen, setIsLegalCheckOpen] = useState(false);
  const [legalCheckContent, setLegalCheckContent] = useState('');
  const [legalCheckResult, setLegalCheckResult] = useState(null);
  const [checkingLegal, setCheckingLegal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    description: '',
    start_date: null,
    end_date: null,
    budget: '',
    target_audience: '',
    content: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await campaignsAPI.getAll();
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      budget: parseFloat(formData.budget) || 0,
      start_date: formData.start_date?.toISOString() || null,
      end_date: formData.end_date?.toISOString() || null
    };
    
    try {
      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, submitData);
        toast.success('Campaign updated');
      } else {
        await campaignsAPI.create(submitData);
        toast.success('Campaign created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      type: campaign.type,
      description: campaign.description || '',
      start_date: campaign.start_date ? new Date(campaign.start_date) : null,
      end_date: campaign.end_date ? new Date(campaign.end_date) : null,
      budget: campaign.budget?.toString() || '',
      target_audience: campaign.target_audience || '',
      content: campaign.content || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await campaignsAPI.delete(id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await campaignsAPI.update(campaignId, { status: newStatus });
      toast.success('Campaign status updated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLegalCheck = async () => {
    if (!legalCheckContent.trim()) {
      toast.error('Please enter content to check');
      return;
    }
    
    setCheckingLegal(true);
    setLegalCheckResult(null);
    
    try {
      const response = await legalCheckAPI.check(legalCheckContent);
      setLegalCheckResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Legal check failed');
    } finally {
      setCheckingLegal(false);
    }
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      type: 'email',
      description: '',
      start_date: null,
      end_date: null,
      budget: '',
      target_audience: '',
      content: ''
    });
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-600',
    scheduled: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-purple-100 text-purple-700'
  };

  const typeColors = {
    email: 'bg-blue-100 text-blue-700',
    social: 'bg-pink-100 text-pink-700',
    ads: 'bg-amber-100 text-amber-700',
    event: 'bg-purple-100 text-purple-700',
    other: 'bg-slate-100 text-slate-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">Manage marketing campaigns</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isLegalCheckOpen} onOpenChange={setIsLegalCheckOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="legal-check-btn">
                <Shield className="h-4 w-4 mr-2" />
                AI Legal Check
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>AI Legal Compliance Check</DialogTitle>
                <DialogDescription>
                  Check your marketing content for Canadian advertising compliance
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Content to Check</Label>
                  <Textarea
                    value={legalCheckContent}
                    onChange={(e) => setLegalCheckContent(e.target.value)}
                    placeholder="Paste your marketing copy, email content, or ad text here..."
                    rows={6}
                    data-testid="legal-content-input"
                  />
                </div>
                <Button 
                  onClick={handleLegalCheck} 
                  disabled={checkingLegal}
                  data-testid="run-legal-check-btn"
                >
                  {checkingLegal ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Run Compliance Check
                    </>
                  )}
                </Button>

                {legalCheckResult && (
                  <Card className="mt-4">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {legalCheckResult.status === 'approved' && (
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                          )}
                          {legalCheckResult.status === 'rejected' && (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                          {legalCheckResult.status === 'needs_review' && (
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                          )}
                          Compliance Result
                        </CardTitle>
                        <Badge className={
                          legalCheckResult.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          legalCheckResult.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {legalCheckResult.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Compliance Score</Label>
                        <div className="flex items-center gap-3 mt-1">
                          <Progress value={legalCheckResult.compliance_score} className="flex-1" />
                          <span className="font-bold">{legalCheckResult.compliance_score}%</span>
                        </div>
                      </div>

                      {legalCheckResult.issues.length > 0 && (
                        <div>
                          <Label className="text-sm text-red-600">Issues Found</Label>
                          <ul className="mt-1 space-y-1">
                            {legalCheckResult.issues.map((issue, index) => (
                              <li key={index} className="text-sm flex items-start gap-2">
                                <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                                {issue}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {legalCheckResult.suggestions.length > 0 && (
                        <div>
                          <Label className="text-sm text-blue-600">Suggestions</Label>
                          <ul className="mt-1 space-y-1">
                            {legalCheckResult.suggestions.map((suggestion, index) => (
                              <li key={index} className="text-sm flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-campaign-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create Campaign'}</DialogTitle>
                <DialogDescription>
                  {editingCampaign ? 'Update campaign details' : 'Set up a new marketing campaign'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Campaign Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    data-testid="campaign-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select value={formData.type} onValueChange={(v) => handleChange('type', v)}>
                      <SelectTrigger data-testid="campaign-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="social">Social Media</SelectItem>
                        <SelectItem value="ads">Paid Ads</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget ($)</Label>
                    <Input
                      id="budget"
                      type="number"
                      value={formData.budget}
                      onChange={(e) => handleChange('budget', e.target.value)}
                      data-testid="campaign-budget-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={2}
                    data-testid="campaign-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="campaign-start-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.start_date ? format(formData.start_date, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => handleChange('start_date', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="campaign-end-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.end_date ? format(formData.end_date, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => handleChange('end_date', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_audience">Target Audience</Label>
                  <Input
                    id="target_audience"
                    value={formData.target_audience}
                    onChange={(e) => handleChange('target_audience', e.target.value)}
                    placeholder="e.g., Small businesses, Enterprise, etc."
                    data-testid="campaign-audience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Content / Copy</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    rows={4}
                    placeholder="Campaign content, email copy, ad text..."
                    data-testid="campaign-content-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="campaign-submit-btn">
                    {editingCampaign ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="campaign-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="campaign-filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.length > 0 ? (
                filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} className="data-table-row" data-testid={`campaign-row-${campaign.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-sm bg-purple-100 flex items-center justify-center">
                          <Megaphone className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          {campaign.target_audience && (
                            <p className="text-xs text-muted-foreground">{campaign.target_audience}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={typeColors[campaign.type]}>
                        {campaign.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        {formatCurrency(campaign.budget)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.start_date ? (
                        <div className="text-sm">
                          <div>{format(new Date(campaign.start_date), 'MMM d')}</div>
                          {campaign.end_date && (
                            <div className="text-muted-foreground">to {format(new Date(campaign.end_date), 'MMM d')}</div>
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={campaign.status}
                        onValueChange={(v) => handleStatusChange(campaign.id, v)}
                      >
                        <SelectTrigger className="h-7 w-[120px]">
                          <Badge className={statusColors[campaign.status]}>
                            {campaign.status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="paused">Paused</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(campaign)}
                          data-testid={`edit-campaign-${campaign.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(campaign.id)}
                          data-testid={`delete-campaign-${campaign.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No campaigns found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignsPage;
